import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Icon, Tabs, Text } from '@factorialco/deprecated-design-system'
import {
  AllValidEmployeesDocument,
  ProcessDatevImportedEmployeesDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import IntegrationCodes from 'modules/marketplace/lib/integrationCodes'

import ConfirmModal from './ConfirmModal'
import ErroredEmployees from './ErroredEmployees'
import ValidEmployees from './ValidEmployees'

const TABS = {
  READY_TO_IMPORT: 'ready-to-import',
  WITH_ERRORS: 'with-errors',
}

const EMPLOYEE_TYPE = {
  VALID_EMPLOYEES: 'valid_employees',
  ERRORED_EMPLOYEES: 'errored_employees',
}

const ImportDatevLauds = () => {
  const params = useParams()
  const employeeImportId = String(params.employee_import_id)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const { data: allValidEmployees } = useQuery(AllValidEmployeesDocument, {
    variables: {
      employeeImportId: employeeImportId,
      employeeType: EMPLOYEE_TYPE.VALID_EMPLOYEES,
    },
  })

  const { data: allErroredEmployees } = useQuery(AllValidEmployeesDocument, {
    variables: {
      employeeImportId: employeeImportId,
      employeeType: EMPLOYEE_TYPE.ERRORED_EMPLOYEES,
    },
  })

  const [tab, setTab] = useState(TABS.READY_TO_IMPORT)
  const [cancelModal, setCancelModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const validCount =
    allValidEmployees?.integrationsDatevLauds?.importedEmployeesConnection?.nodes?.length || 0
  const erroredCount =
    allErroredEmployees?.integrationsDatevLauds?.importedEmployeesConnection?.nodes?.length || 0
  const tabs = [
    {
      tabId: TABS.READY_TO_IMPORT,
      label: `${i18n.t(
        'payroll_integrations.incidences.datev_lauds.import.tabs.ready_to_import'
      )} (${validCount})`,
    },
    {
      tabId: TABS.WITH_ERRORS,
      label: `${i18n.t(
        'payroll_integrations.incidences.datev_lauds.import.tabs.with_errors'
      )} (${erroredCount})`,
    },
  ]

  const navigateToEmployees = () => {
    navigate(navigation.employees.list.fullPath())
  }

  const { running, performAction: apiGenerateExport } = useAsyncAction(async () => {
    const api = ApiClient()
    const employeeType: string =
      tab === TABS.READY_TO_IMPORT ? EMPLOYEE_TYPE.VALID_EMPLOYEES : EMPLOYEE_TYPE.ERRORED_EMPLOYEES

    const downloadable = await api.get(
      `/integrations/${IntegrationCodes.datev_lauds_factorial}/can_download_export?employee_import_id=${params.employee_import_id}&employee_type=${employeeType}`
    )
    if (downloadable) {
      window.location.href = buildUrl(
        `/integrations/${IntegrationCodes.datev_lauds_factorial}/datev_employees_export?employee_import_id=${params.employee_import_id}&employee_type=${employeeType}`
      )
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('payroll_integrations.incidences.datev_lauds.import.export.error'),
      })
    }
  })

  const [
    processIntegrationsDatevlaudsEmployeeImport,
    { loading: processingIntegrationsDatevLaudsEmployeeImport },
  ] = useMutation(ProcessDatevImportedEmployeesDocument, {
    extractorFn: (data) =>
      data.integrationsDatevLaudsMutations.processIntegrationsdatevlaudsEmployeeImport,
  })

  const delegateSave = async () => {
    if (erroredCount > 0) {
      setConfirmModal(true)
    } else {
      await saveEmployees()
    }
  }

  const saveEmployees = async () => {
    const result = await processIntegrationsDatevlaudsEmployeeImport({
      variables: {
        id: employeeImportId,
      },
    })

    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('payroll_integrations.incidences.datev_lauds.import.started'),
      })
      setConfirmModal(false)
      navigateToEmployees()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('payroll_integrations.incidences.datev_lauds.import.started'),
      })
    }
  }

  return (
    <Box flexDirection="column" height="full" overflowY="hidden" gap="s8">
      <Box flexDirection="row" alignItems="flexStart" width={'full'} paddingBottom="s20">
        <Text.Header1 color="grey1000" weight="bold">
          {i18n.t('payroll_integrations.incidences.datev_lauds.import.title')}
        </Text.Header1>
      </Box>

      <Box height="full" overflowY="hidden">
        <Tabs tabsAlign="flexStart" onTabClicked={(tabId: string) => setTab(tabId)} currentId={tab}>
          {tabs.map(({ tabId, label }) => (
            <Tabs.Tab key={tabId} id={tabId} label={label}>
              <Box paddingTop="s16" />
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                background="viridian100"
                paddingX="s12"
                paddingY="s12"
                borderRadius={{ all: 'abs012' }}
              >
                <Box flexDirection="row" justifyContent="spaceAround" alignItems="center">
                  <Icon.Medium icon="lightbulb" color="grey1000" />
                  <Box>
                    {tabId === TABS.READY_TO_IMPORT &&
                      i18n.t(
                        'payroll_integrations.incidences.datev_lauds.import.callout.description'
                      )}
                    {tabId === TABS.WITH_ERRORS &&
                      i18n.t(
                        'payroll_integrations.incidences.datev_lauds.import.callout.description_error'
                      )}
                  </Box>
                </Box>

                <Box>
                  <Button
                    type="secondary"
                    iconName="download"
                    disabled={running}
                    onClick={apiGenerateExport}
                  >
                    {i18n.t('payroll_integrations.incidences.datev_lauds.import.callout.button')}
                  </Button>
                </Box>
              </Box>
              {tabId === TABS.READY_TO_IMPORT && (
                <Box position="relative" overflowY="scroll">
                  <ValidEmployees employeeImportId={employeeImportId} />
                </Box>
              )}

              {tabId === TABS.WITH_ERRORS && (
                <Box position="relative" overflowY="scroll">
                  <ErroredEmployees employeeImportId={employeeImportId} />
                </Box>
              )}
            </Tabs.Tab>
          ))}
        </Tabs>
      </Box>

      <Box
        height="s64"
        paddingX="s32"
        flexDirection="row"
        justifyContent="flexEnd"
        paddingTop="s16"
        gap="s8"
        border={{
          top: {
            width: 's1',
            style: 'solid',
            color: 'darker160',
          },
        }}
      >
        <Button type="secondary" onClick={() => setCancelModal(true)}>
          {i18n.t('payroll_integrations.incidences.datev_lauds.import.buttons.cancel')}
        </Button>
        <Button onClick={delegateSave} disabled={processingIntegrationsDatevLaudsEmployeeImport}>
          {i18n.t('payroll_integrations.incidences.datev_lauds.import.buttons.import', {
            employeesCount: validCount,
          })}
        </Button>
      </Box>

      {cancelModal && (
        <Modal
          title={i18n.t('payroll_integrations.incidences.datev_lauds.import.modals.cancel.title')}
          description={i18n.t(
            'payroll_integrations.incidences.datev_lauds.import.modals.cancel.description'
          )}
          illustration="alert"
          onClose={() => setCancelModal(true)}
          footer={
            <>
              <Button type="secondary" onClick={() => setCancelModal(true)}>
                {i18n.t('payroll_integrations.incidences.datev_lauds.import.buttons.go_back')}
              </Button>
              <Button onClick={() => navigateToEmployees()}>
                {i18n.t('payroll_integrations.incidences.datev_lauds.import.buttons.cancel')}
              </Button>
            </>
          }
        />
      )}

      {confirmModal && (
        <ConfirmModal setConfirmModal={setConfirmModal} saveEmployees={saveEmployees} />
      )}
    </Box>
  )
}

export default ImportDatevLauds
